"use client";

import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { BookCall } from "@/components/booking/BookCall";
import { buttonVariants } from "@/components/ui/button";
import { BOOKING_INTRO } from "@/lib/booking";
import { SplitHover } from "@/components/ui/SplitHover";
import { CONTACT } from "@/lib/constants";
import { useInViewOnce } from "@/lib/useInViewOnce";
import { cn } from "@/lib/utils";

const EASE = [0.16, 1, 0.3, 1] as const;

const mailtoFor = () => {
  const subject = "Project for JRAT'S Studio";
  const body = [
    "Hi JRAT'S team,",
    "",
    "What we're building: ",
    "What's slowing us down: ",
    "Best time for a call: ",
  ].join("\n");
  return `mailto:${CONTACT.EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

/**
 * The closing call to action. The headline animates in, the booking intro (why the
 * questions on the booking form are worth answering) follows, and the CTA buttons sit below.
 */
export const BuildPicker = () => {
  const reduce = useReducedMotion();
  const [ref, seen] = useInViewOnce<HTMLDivElement>();
  const [settled, setSettled] = useState(false);

  const shown = reduce || seen;
  const unclipped = reduce || settled;

  /** Matches SectionHeader: a line riding up from behind its clipped edge. */
  const line = (delay: number) => ({
    initial: { y: "110%" },
    animate: shown ? { y: "0%" } : { y: "110%" },
    transition: { duration: reduce ? 0 : 0.8, ease: EASE, delay: reduce ? 0 : delay },
  });

  return (
    <div ref={ref}>
      {/* ── The headline ─────────────────────────────────────────────── */}
      <div>
        {/* Each line gets its own mask so the second rides in behind the first. */}
        <h2 className="font-heading text-3xl uppercase leading-[0.85] tracking-tighter text-brand-ink sm:text-4xl sm:leading-[0.8] md:text-6xl lg:text-[104px]">
          <span className={cn("line-mask", unclipped && "line-mask--done")}>
            <motion.span {...line(0)} className="block">
              <SplitHover>Tell us what</SplitHover>
            </motion.span>
          </span>
          <span className={cn("line-mask", unclipped && "line-mask--done")}>
            <motion.span
              {...line(0.08)}
              onAnimationComplete={() => shown && setSettled(true)}
              className="block"
            >
              <SplitHover>you&apos;re building.</SplitHover>
            </motion.span>
          </span>
        </h2>

        <motion.div
          aria-hidden
          initial={{ scaleX: 0 }}
          animate={shown ? { scaleX: 1 } : { scaleX: 0 }}
          transition={{ duration: reduce ? 0 : 0.7, ease: EASE, delay: reduce ? 0 : 0.25 }}
          className="mt-4 h-1.5 w-16 origin-left bg-brand-mint sm:mt-5 sm:h-2 sm:w-24 md:mt-6 md:w-32"
        />

        <p className="mt-8 max-w-xl text-base leading-relaxed text-brand-ink/75 md:mt-10 md:text-lg">
          <span className="font-semibold text-brand-ink">{BOOKING_INTRO.lead}</span> {BOOKING_INTRO.body}
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
          <BookCall className={cn(buttonVariants(), "group")}>
            <span className="grid">
              <span className="col-start-1 row-start-1 inline-flex items-center justify-center gap-2 transition-all duration-300 group-hover:-translate-y-1.5 group-hover:opacity-0">
                Grab 30 minutes
                <ArrowUpRight className="size-4" />
              </span>
              <span
                aria-hidden
                className="col-start-1 row-start-1 inline-flex translate-y-1.5 items-center justify-center gap-2 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100"
              >
                No pitch, promise
                <ArrowUpRight className="size-4" />
              </span>
            </span>
          </BookCall>
          <a
            href={mailtoFor()}
            className="group inline-flex items-center gap-2 font-mono text-sm uppercase tracking-widest text-brand-accent transition-colors duration-300 hover:text-brand-ink"
          >
            {CONTACT.EMAIL}
            <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
        </div>
      </div>
    </div>
  );
};
